import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Grid,
    Pagination,
    Stack,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    CheckCircleOutline as ApproveIcon,
    AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
    fetchClients, 
    deleteClient, 
    toggleClientStatus, 
    approveClient,
    setFilters, 
    clearFilters 
} from '../../store/slices/clientSlice';
import { Client } from '../../services/clientApi';
import { useTheme } from '../../contexts/ThemeContext';
import ClientForm from './ClientForm';
import ClientView from './ClientView';
import ClientTariffs from './ClientTariffs';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

const Clients: React.FC = () => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const dispatch = useAppDispatch();
    const { toast, showSuccess, showError, hideToast } = useToast();

    const { clients, pagination, filters, loading, error } = useAppSelector((state) => state.clients);

    // Local state
    const [searchQuery, setSearchQuery] = useState(filters.query);
    const [selectedStatus, setSelectedStatus] = useState(filters.status);
    const [currentPage, setCurrentPage] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openTariffs, setOpenTariffs] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

    // Load clients on component mount
    useEffect(() => {
        loadClients();
    }, [currentPage, filters]);

    const loadClients = () => {
        const params = {
            page: currentPage,
            per_page: 20,
            ...filters
        };
        dispatch(fetchClients(params));
    };

    // Handle search
    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setCurrentPage(1);
    };

    // Handle filter changes
    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSelectedStatus(value);
        dispatch(setFilters({ status: value }));
        setCurrentPage(1);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedStatus('');
        dispatch(clearFilters());
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Handle client actions
    const handleViewClient = (client: Client) => {
        setSelectedClientId(client.id);
        setOpenView(true);
    };

    // Handle view client tariffs
    const handleViewTariffs = (client: Client) => {
        setSelectedClient(client);
        setOpenTariffs(true);
    };

    const handleEditClient = (client: Client) => {
        setSelectedClient(client);
        setOpenForm(true);
    };

    const handleDeleteClient = (client: Client) => {
        setClientToDelete(client);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (clientToDelete) {
            try {
                await dispatch(deleteClient(clientToDelete.id)).unwrap();
                showSuccess(`Client "${clientToDelete.name}" has been deleted successfully`);
                loadClients();
            } catch (err: any) {
                showError(err?.message || 'Failed to delete client');
            } finally {
                setDeleteDialog(false);
                setClientToDelete(null);
            }
        }
    };

    const handleToggleStatus = async (client: Client) => {
        try {
            const result = await dispatch(toggleClientStatus(client.id)).unwrap();
            const newStatus = result.status === 1 ? 'activated' : 'deactivated';
            showSuccess(`Client "${client.name}" has been ${newStatus} successfully`);
            loadClients();
        } catch (err: any) {
            showError(err?.message || 'Failed to update client status');
        }
    };

    const handleApproveClient = async (client: Client) => {
        try {
            await dispatch(approveClient(client.id)).unwrap();
            showSuccess(`Client "${client.name}" has been approved successfully`);
            loadClients();
        } catch (err: any) {
            showError(err?.message || 'Failed to approve client');
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get status chip
    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                icon={<ActiveIcon />}
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                icon={<InactiveIcon />}
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <Box sx={{ p: isExtraSmall ? 0.5 : isMobile ? 1 : 3 }}>
            {/* Header */}
            <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 2 : 0
            }}>
                <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 600,
                        textAlign: isMobile ? 'center' : 'left'
                    }}
                >
                    Client Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setSelectedClient(null);
                        setOpenForm(true);
                    }}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        }
                    }}
                >
                    {isMobile ? 'Add Client' : 'Add New Client'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: theme.palette.background.paper }}>
                <CardContent sx={{ p: isExtraSmall ? 1 : isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                            <TextField
                                fullWidth
                                label="Search by name or company"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="Status"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="1">Active</MenuItem>
                                    <MenuItem value="0">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                fullWidth
                                sx={{ borderRadius: 3 }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Clients Table */}
            <Card sx={{ bgcolor: theme.palette.background.paper, display: isMobile ? 'none' : 'block' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ position: 'relative', minHeight: 400 }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}

                        <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clients.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" color="textSecondary">
                                                    No clients found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        clients.map((client) => (
                                            <TableRow key={client.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {client.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {client.client_profile?.company_name || '-'}
                                                </TableCell>
                                                <TableCell>{client.email}</TableCell>
                                                <TableCell>
                                                    {client.client_profile?.authorized_phone || '-'}
                                                    {client.client_profile?.admin_phone && (
                                                        <Typography variant="caption" display="block" color="textSecondary">
                                                            {client.client_profile.admin_phone}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {client.client_profile?.areaZone?.name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {client.branch?.name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(client.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusChip(client.status)}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewClient(client)}
                                                                sx={{ color: theme.palette.primary.main }}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Tariffs">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewTariffs(client)}
                                                                sx={{ color: theme.palette.info.main }}
                                                            >
                                                                <MoneyIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit Client">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditClient(client)}
                                                                sx={{ color: theme.palette.secondary.main }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {!client.status && (
                                                            <Tooltip title="Approve Client">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleApproveClient(client)}
                                                                    sx={{ color: theme.palette.success.main }}
                                                                >
                                                                    <ApproveIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title={client.status ? 'Deactivate' : 'Activate'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleStatus(client)}
                                                                sx={{ 
                                                                    color: client.status 
                                                                        ? theme.palette.error.main 
                                                                        : theme.palette.success.main 
                                                                }}
                                                            >
                                                                {client.status ? <InactiveIcon /> : <ActiveIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Client">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteClient(client)}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Pagination */}
                    {pagination && pagination.last_page > 1 && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={pagination.last_page}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton={!isMobile}
                                showLastButton={!isMobile}
                                size={isMobile ? "small" : "medium"}
                                siblingCount={isMobile ? 0 : 1}
                            />
                        </Box>
                    )}

                    {/* Results Summary */}
                    {pagination && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {pagination.from} to {pagination.to} of {pagination.total} clients
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Mobile Card Layout */}
            {isMobile && (
                <Card sx={{ bgcolor: theme.palette.background.paper, mt: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ position: 'relative', minHeight: 200 }}>
                            {loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 1,
                                        borderRadius: 3
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            {clients.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No clients found
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={isMobile ? 1 : 2}>
                                    {clients.map((client) => (
                                        <Card 
                                            key={client.id} 
                                            sx={{ 
                                                bgcolor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 3
                                            }}
                                        >
                                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1 : 2 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 0.5 : 1 }}>
                                                            {client.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: isMobile ? 0.25 : 0.5 }}>
                                                            {client.client_profile?.company_name || 'No Company'}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: isMobile ? 0.5 : 1 }}>
                                                            {client.email}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}>
                                                            {getStatusChip(client.status)}
                                                            {!client.status && (
                                                                <Chip
                                                                    label="Pending Approval"
                                                                    color="warning"
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
                                                        <IconButton
                                                            size={isMobile ? "small" : "small"}
                                                            onClick={() => handleViewClient(client)}
                                                            sx={{ 
                                                                color: theme.palette.primary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewTariffs(client)}
                                                            sx={{ 
                                                                color: theme.palette.info.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <MoneyIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditClient(client)}
                                                            sx={{ 
                                                                color: theme.palette.secondary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        {!client.status && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleApproveClient(client)}
                                                                sx={{ 
                                                                    color: theme.palette.success.main,
                                                                    bgcolor: theme.palette.action.hover,
                                                                    '&:hover': { bgcolor: theme.palette.action.selected }
                                                                }}
                                                            >
                                                                <ApproveIcon />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleStatus(client)}
                                                            sx={{ 
                                                                color: client.status 
                                                                    ? theme.palette.error.main 
                                                                    : theme.palette.success.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            {client.status ? <InactiveIcon /> : <ActiveIcon />}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteClient(client)}
                                                            sx={{ 
                                                                color: theme.palette.error.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                                <Divider sx={{ my: isMobile ? 1 : 2 }} />
                                                <Grid container spacing={isMobile ? 1 : 2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Phone
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {client.client_profile?.authorized_phone || '-'}
                                                        </Typography>
                                                        {client.client_profile?.admin_phone && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {client.client_profile.admin_phone}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Area Zone
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {client.client_profile?.areaZone?.name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Branch
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {client.branch?.name || '-'}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Created
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(client.created_at)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Mobile Pagination */}
            {isMobile && pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton={false}
                        showLastButton={false}
                        size="small"
                        siblingCount={0}
                    />
                </Box>
            )}

            {/* Mobile Results Summary */}
            {isMobile && pagination && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} clients
                    </Typography>
                </Box>
            )}

            {/* Client Form Dialog */}
            <ClientForm
                open={openForm}
                client={selectedClient}
                onClose={() => {
                    setOpenForm(false);
                    setSelectedClient(null);
                }}
                onSuccess={(isEdit: boolean, clientName: string) => {
                    setOpenForm(false);
                    setSelectedClient(null);
                    loadClients();
                    if (isEdit) {
                        showSuccess(`Client "${clientName}" has been updated successfully`);
                    } else {
                        showSuccess(`Client "${clientName}" has been created successfully`);
                    }
                }}
                onError={(message: string) => {
                    showError(message);
                }}
            />

            {/* Client View Dialog */}
            <ClientView
                open={openView}
                clientId={selectedClientId}
                onClose={() => {
                    setOpenView(false);
                    setSelectedClientId(null);
                }}
            />

            {/* Client Tariffs Dialog */}
            <ClientTariffs
                open={openTariffs}
                clientId={selectedClient?.id || 0}
                clientName={selectedClient?.name || ''}
                onClose={() => {
                    setOpenTariffs(false);
                    setSelectedClient(null);
                }}
                onSuccess={(message: string) => {
                    showSuccess(message);
                }}
                onError={(message: string) => {
                    showError(message);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete Client"
                message={`Are you sure you want to delete "${clientToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setClientToDelete(null);
                }}
            />

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default Clients;
